// ══════════════════════════════════════════════════════
//  teamEnhanced.js — 团队增强分析（Phase 1：Layer A + Layer C）
//
//  解决问题：早期项目 RootData 团队数据缺失或不完整。
//
//  Phase 1 数据源：
//    Layer A: 项目官网 /team /about /people 页 → Claude 提取结构化创始人信息
//    Layer C: GitHub 仓库前 3 名贡献者 → bio + 提交模式 + 跨链接 Twitter/website
//
//  Phase 2（下次）：Layer B Twitter 推文深度分析 + Layer D Brave 兜底搜索 + Claude 合成 + verdict 红绿旗
// ══════════════════════════════════════════════════════

import { RD_BASE } from './config';
import { surfWebSearch } from './surf';

// ── 常量 ──────────────────────────────────────────────
const TEAM_PAGE_SLUGS = ['team', 'about', 'about-us', 'people', 'company', 'who-we-are', 'meet-the-team'];
const HTML_CAP_CHARS  = 60_000; // Claude prompt 限制 ~10500 chars，HTML 给 60k 让正则压缩到 ~8000

// ── 工具：从 URL 衍生候选团队页 URL ───────────────────
export function deriveTeamPageCandidates(websiteUrl) {
  if (!websiteUrl) return [];
  let base;
  try {
    base = new URL(websiteUrl).origin;
  } catch { return []; }
  return TEAM_PAGE_SLUGS.map(slug => `${base}/${slug}`);
}

// ── 工具：HTML → 文本（剥离脚本 + framer/webflow 噪音 + 保留语义）
//  Framer/Webflow 等 SPA 站每个文本节点周围带 ~200 字符的 css/data-framer
//  噪音，单靠 strip script/style 不够，会把团队成员名字埋进无数 class/style
//  属性里干扰 Claude 抽取。此函数做更激进的清理：
//    - 移除 class=, style=, id= 属性
//    - 移除所有 data-* 属性（含 data-framer-name —— EDGE Markets 教训：
//      Framer 老原型残留 data-framer-name="Benjamin Miller" 看起来像人名，
//      data-framer-name="Engineering" 看起来像 section，属性值不一致让
//      模型困惑，DeepSeek/Claude 都容易输出 founders=[]）
//    - 移除 srcset, sizes, loading, draggable 等图片/lazy-load 噪音
//    - 移除空白 <span>/<div> 包装
//  目标：把 ~265KB 压到 30-50KB，文本密度提升 3-5x。
export function compressHtml(html) {
  if (!html) return '';
  return html
    .replace(/<!--[\s\S]*?-->/g, ' ')
    .replace(/<script[\s\S]*?<\/script>/gi, ' ')
    .replace(/<style[\s\S]*?<\/style>/gi, ' ')
    .replace(/<noscript[\s\S]*?<\/noscript>/gi, ' ')
    .replace(/<svg[\s\S]*?<\/svg>/gi, ' ')
    .replace(/<picture[\s\S]*?<\/picture>/gi, ' ') // 图片源集合噪音大无内容
    // 关键：剥 framer/webflow/tailwind 风格属性噪音
    .replace(/\s+class\s*=\s*"[^"]*"/gi, '')
    .replace(/\s+style\s*=\s*"[^"]*"/gi, '')
    .replace(/\s+id\s*=\s*"[^"]*"/gi, '')
    .replace(/\s+data-[a-z-]+\s*=\s*"[^"]*"/gi, '') // 全删 data-* 包括 data-framer-name
    .replace(/\s+(srcset|sizes|loading|decoding|draggable|fetchpriority|crossorigin)\s*=\s*"[^"]*"/gi, '')
    .replace(/\s+(aria-[a-z-]+)\s*=\s*"[^"]*"/gi, '')
    .replace(/\s+(role|tabindex)\s*=\s*"[^"]*"/gi, '')
    // 合并连续空标签
    .replace(/<(div|span)\s*>(\s*<\/\1>\s*)+/gi, ' ')
    .replace(/\s+/g, ' ')
    .trim()
    .slice(0, HTML_CAP_CHARS);
}

// ── 项目名 → 备用站点候选（Q54）─────────────────────────────────
//  背景：RootData 给的 social_media.website 经常是项目"基金会"站（如
//  hyperfoundation.org），而真正放团队页的是产品站（hyperliquid.xyz）。
//  从项目名 slug 派生 4-5 个常见 TLD 候选，让 Layer A 多一次机会。
//  现有 primary URL 已包含的根域名会被去重，避免重复抓。
export function deriveProjectAlternateSites(projectName, primaryUrl) {
  if (!projectName || typeof projectName !== 'string') return [];
  // 把项目名压成小写 slug：去空格、连字符规范化
  const slug = projectName.toLowerCase().replace(/[^a-z0-9]+/g, '');
  if (slug.length < 3 || slug.length > 30) return []; // 太短/太长 不靠谱
  const COMMON_TLDS = ['xyz', 'io', 'com', 'app', 'finance', 'network'];
  // 去重：从 primaryUrl 抽 root domain stem
  let primaryStem = '';
  try {
    if (primaryUrl) {
      const u = new URL(primaryUrl.startsWith('http') ? primaryUrl : `https://${primaryUrl}`);
      primaryStem = u.hostname.replace(/^www\./, '').split('.')[0];
    }
  } catch {}
  const sites = [];
  for (const tld of COMMON_TLDS) {
    const domain = `${slug}.${tld}`;
    // primary stem 完全匹配就跳过（避免重复 hyperliquid.xyz 时打 hyperliquid.xyz）
    if (primaryStem === slug) continue;
    sites.push(`https://${domain}`);
  }
  return sites.slice(0, 4); // 限制 4 个，控制延迟
}

// ── Layer A: 试探官网团队页，取第一个 200 + 含"团队"语义的 ─
//  策略：候选 URL 并行抓，按 TEAM_PAGE_SLUGS 顺序优先取首个命中。
//  per-probe 超时 14s（Framer/Webflow 等富前端站点常见 5-10s 首字节延迟），
//  proxy 端 fetch-html upstream timeout 是 15s，整体 ≤ 15s。
//  超时返回 null，不阻塞 analyze。
export async function probeTeamPage(websiteUrl, debug = null) {
  const candidates = deriveTeamPageCandidates(websiteUrl);
  if (!candidates.length) return null;

  const KEYWORDS = /\b(team|founder|co-founder|founding|cto|ceo|cofounder|leadership|our people|meet the team|创始人|团队)\b/i;
  const probeOne = async (url) => {
    const probeRecord = { url, ok: false, status: null, htmlLen: 0, hasKeywords: false, error: null };
    try {
      const r = await fetch(`${RD_BASE}/api/fetch-html?url=${encodeURIComponent(url)}`, {
        signal: typeof AbortSignal !== 'undefined' && AbortSignal.timeout ? AbortSignal.timeout(14000) : undefined,
      });
      probeRecord.status = r.status;
      if (!r.ok) { probeRecord.error = `fetch-html ${r.status}`; return null; }
      const data = await r.json();
      if (!data?.ok || !data.html) { probeRecord.error = 'no html'; return null; }
      probeRecord.ok = true;
      probeRecord.htmlLen = data.html.length;
      // 注：proxy 现在服务端 follow 3 hop 重定向，data.redirected 字段已废弃
      // 旧 redirected:true 直接跳过的逻辑保留（兼容老缓存路径），但默认走 follow 路径
      if (data.redirected) { probeRecord.error = 'redirected (legacy)'; return null; }
      probeRecord.hasKeywords = KEYWORDS.test(data.html);
      if (!probeRecord.hasKeywords) return null;
      return { url, html: data.html };
    } catch (e) { probeRecord.error = e?.message || 'fetch error'; return null; }
    finally { if (debug) debug.probedCandidates.push(probeRecord); }
  };

  const results = await Promise.all(candidates.map(probeOne));
  const winner = results.find(Boolean) || null;
  if (debug) debug.winnerUrl = winner?.url || null;
  return winner;
}

// ── Layer A: 让 Claude 从 HTML 提取结构化团队信息 ──────
//  输出 schema: { founders: [{name, role, bio, twitter, github, linkedin, priorWork[]}] }
//  失败返回 null。
export async function extractTeamFromHtml(htmlContent, projectName, sourceUrl, debug = null) {
  if (!htmlContent) return null;
  const compressed = compressHtml(htmlContent);
  if (!compressed) return null;
  if (debug) {
    debug.compressedLen = compressed.length;
    debug.compressedSample = compressed.slice(0, 1500); // 前 1500 字符够看团队 section
  }

  const prompt = `从下方 HTML 提取「${projectName}」项目的团队/创始人信息。
严格按 JSON 格式输出，不要任何额外解释。

⚠️ 关键：所有人名在 <p> 标签里。HTML 里相邻 <p> 通常是"姓名 + 角色"。
同时还要抓取：个人介绍 bio / 头像 avatarUrl / 个人详情链接 profileUrl /
社交账号 twitter/github/linkedin。

## 示例 1（Framer 团队页 — 必须能识别）

输入片段：
<a href="./about/seni-thomas">
  <div><img src="https://framerusercontent.com/images/abc.jpg" alt></div>
  <div><p>Seni Thomas</p></div>
  <div><p>CEO + Founder</p></div>
</a>

期望输出（profileUrl 是 about 链接，avatarUrl 是 img src）：
{
  "founders": [
    {
      "name":"Seni Thomas",
      "role":"CEO + Founder",
      "bio":"",
      "avatarUrl":"https://framerusercontent.com/images/abc.jpg",
      "profileUrl":"./about/seni-thomas",
      "twitter":"","github":"","linkedin":"","priorWork":[]
    }
  ]
}

## 示例 2（传统团队页 + bio + 社交链接）

输入片段：
<div>
  <img src="/images/alice.png" />
  <h2>Alice Wong, CEO</h2>
  <p>Previously at Coinbase 2018-2022. Led lending markets team. Stanford CS PhD.</p>
  <a href="https://twitter.com/alicew">@alicew</a>
  <a href="https://linkedin.com/in/alice-wong">LinkedIn</a>
</div>

期望输出：
{
  "founders": [
    {
      "name":"Alice Wong",
      "role":"CEO",
      "bio":"Previously at Coinbase 2018-2022, led lending markets team. Stanford CS PhD.",
      "avatarUrl":"/images/alice.png",
      "profileUrl":"",
      "twitter":"@alicew",
      "github":"",
      "linkedin":"https://linkedin.com/in/alice-wong",
      "priorWork":["At Coinbase 2018-22","Stanford CS PhD"]
    }
  ]
}

## 现在轮到你提取这个 HTML：
${compressed}

## 提取规则：
1. **姓名识别**：两个或更多英文单词，首字母大写（如 "Seni Thomas"）。
   不接受单个单词、全小写、含特殊符号。
2. **角色识别**：CEO / CTO / Founder / Co-Founder / Engineer / Developer /
   Designer / Operations / Head of XXX / Lead XXX / Engineering / Product。
   早期项目工程师就是创始团队，必须收。
3. **过滤**：Advisor / Investor / Partner / Backer / Customer / Press。
4. **avatarUrl**：找该 founder 容器内的 <img src="..."> URL。Framer 站常用
   framerusercontent.com，传统站可能 /images/xxx 相对路径。无则空字符串。
5. **profileUrl**：找包裹该 founder 的 <a href="..."> 链接，特征是 /about/、
   /team/、/people/、/members/ 等路径。是相对路径就保留相对路径。无则空。
6. **bio**：限 250 字，从邻近 <p> 标签找完整描述（前作/教育/领域专长）。
   注意只取该 founder 紧邻的描述，不要混入其他人的 bio。无则空。
7. **twitter/github/linkedin**：HTML 里 twitter.com/X / x.com/X /
   github.com/X / linkedin.com/in/X 抽到对应字段。
8. **priorWork**：bio 中明确前作/学校/公司，每条 ≤ 25 字。无则空数组。

## 关键约束：
- **不要编造**：HTML 里没有的人名 / 链接 / 头像不能写。
- **不要因为没 bio 就跳过**：name + role 抓到就先写，其他空字符串。
- **找到的人越多越好**（直到 10 人封顶），不要"不够确定"就漏。

## 输出 JSON 格式（严格）：
{
  "founders": [
    {
      "name": "Full Name",
      "role": "CEO / CTO / Engineer / ...",
      "bio": "≤250 chars or empty string",
      "avatarUrl": "URL or empty string",
      "profileUrl": "URL or path or empty string",
      "twitter": "@handle or empty string",
      "github": "username or empty string",
      "linkedin": "URL or empty string",
      "priorWork": ["item ≤25 chars", "..."]
    }
  ]
}`;

  try {
    const res = await fetch(`${RD_BASE}/api/ai/score`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: 'claude-opus-4-7',
        max_tokens: 1500,
        messages: [{ role: 'user', content: prompt }],
      }),
    });
    if (debug) debug.claudeStatus = res.status;
    if (!res.ok) {
      console.warn(`[team-A-html-ai] ❌ HTTP ${res.status}`);
      if (debug) debug.claudeError = `HTTP ${res.status}`; return null;
    }
    const ai = await res.json();
    // Q60
    if (ai._fallback) console.warn(`[team-A-html-ai] ⚠️ DeepSeek 兜底, 原因: ${ai._fallback_reason || '未知'}`);
    else console.log(`[team-A-html-ai] ✅ ${ai.model || 'claude'} OK`);
    const text = (ai.content || []).map(b => b?.type === 'text' ? b.text : '').join('');
    if (debug) debug.claudeRawText = text.slice(0, 1500);
    const cleaned = text.replace(/```json\s*/g, '').replace(/```\s*/g, '').trim();
    const m = cleaned.match(/\{[\s\S]*\}/);
    if (!m) { if (debug) debug.claudeError = 'no JSON match'; return null; }
    let parsed;
    try { parsed = JSON.parse(m[0]); } catch (e) {
      if (debug) debug.claudeError = `parse fail: ${e.message}`;
      return null;
    }
    const founders = Array.isArray(parsed?.founders) ? parsed.founders : [];
    if (debug) debug.claudeFoundersCount = founders.length;
    // 把相对 profileUrl / avatarUrl 解析成绝对 URL（基于 sourceUrl），UI 直接用
    const resolveAbs = (maybeRelative) => {
      if (!maybeRelative || !sourceUrl) return maybeRelative || '';
      try { return new URL(maybeRelative, sourceUrl).toString(); }
      catch { return maybeRelative; }
    };
    const normalized = founders
      .filter(f => f?.name)
      .slice(0, 10)
      .map(f => ({
        ...f,
        avatarUrl:  resolveAbs(f.avatarUrl  || ''),
        profileUrl: resolveAbs(f.profileUrl || ''),
      }));
    return { founders: normalized, sourceUrl: sourceUrl || null };
  } catch (e) {
    if (debug) debug.claudeError = `exception: ${e.message}`;
    return null;
  }
}

// ── Layer C: 拉 GitHub 仓库前 N 贡献者详情 ────────────
//  走 proxy 的 /api/github-contributors（带 GITHUB_TOKEN，5000次/小时），不再从浏览器
//  直连未认证的 api.github.com（60次/小时/IP）——原来的版本测试/使用密集时很容易被
//  静默限流，project_cache 里绝大多数已分析项目因此完全没抓到贡献者数据，查过生产
//  数据才发现这个问题（见 proxy/server.js 该路由的注释）。
//  返回：[{ username, name, bio, twitter, blog, commitCount, accountCreatedAt, pedigreeSignal }]
//  失败/无 GitHub 时返回空数组。
export async function fetchTopContributors(owner, repo, topN = 3) {
  if (!owner || !repo) return [];
  try {
    const r = await fetch(`${RD_BASE}/api/github-contributors?owner=${encodeURIComponent(owner)}&repo=${encodeURIComponent(repo)}&topN=${topN}`, {
      signal: typeof AbortSignal !== 'undefined' && AbortSignal.timeout ? AbortSignal.timeout(10000) : undefined,
    });
    if (!r.ok) return [];
    const json = await r.json();
    if (!json.ok || !Array.isArray(json.contributors)) return [];
    return json.contributors;
  } catch { return []; }
}

// ── GitHub README 全文 ────────────────────────────────
//  很多早期项目的 README 里已经写清楚产品/技术/团队/联系方式，这些是项目方
//  自己写的第一手真实资料。直接拉纯文本，不经过 AI 加工/复述，作为可引用的
//  真实来源之一喂给下游的引用式 AI 综合（见 earlyBirdWiki.js）。
export async function fetchGithubReadme(owner, repo) {
  if (!owner || !repo) return null;
  try {
    const r = await fetch(`https://api.github.com/repos/${owner}/${repo}/readme`, {
      headers: { Accept: 'application/vnd.github.raw+json' },
      signal: typeof AbortSignal !== 'undefined' && AbortSignal.timeout ? AbortSignal.timeout(6000) : undefined,
    });
    if (!r.ok) return null;
    const text = await r.text();
    return text ? text.slice(0, 6000) : null; // 截断，避免单一来源撑爆 prompt
  } catch { return null; }
}

// ── Twitter bio 网站兜底 ──────────────────────────────
//  当项目 social_media.website 为空 OR Layer A 没找到团队页时，
//  从项目 Twitter bio 提取链接里的网站 URL（profile.url 或 bio 文本里的 URL）。
//
//  例：项目只有 Twitter 没填官网 → Twitter bio 里有 "https://foo.xyz" → 用这个 URL 探测 /team。
export async function getTwitterBioWebsite(twitterHandleOrUrl) {
  if (!twitterHandleOrUrl) return null;
  try {
    const handle = String(twitterHandleOrUrl)
      .replace(/^https?:\/\/(twitter|x)\.com\//i, '')
      .replace(/^@/, '')
      .split('/')[0]
      .split('?')[0]
      .trim();
    if (!handle || handle.length < 2) return null;
    const r = await fetch(`${RD_BASE}/api/twitter-bios?usernames=${encodeURIComponent(handle)}`);
    if (!r.ok) return null;
    const data = await r.json();
    // 兼容 @handle 或 handle 两种 key
    const bioData = data?.bios?.[`@${handle}`] || data?.bios?.[handle];
    if (!bioData) return null;
    // 优先 profile.url（Twitter 资料卡上的网站字段），其次 bioUrls 第一条
    const url = (typeof bioData === 'object' ? bioData.url : null)
      || (Array.isArray(bioData?.bioUrls) && bioData.bioUrls.length ? bioData.bioUrls[0] : null);
    if (!url) return null;
    // 过滤明显不是项目网站的（社交平台/RSS/链接聚合等）
    const NON_PROJECT = /(twitter|x\.com|linktr\.ee|linkin\.bio|t\.me|discord|telegram|reddit|youtube|medium\.com|substack|github\.com|notion\.so|gitbook\.io)/i;
    if (NON_PROJECT.test(url)) return null;
    return url;
  } catch { return null; }
}

/**
 * 拉一个推特账号的完整资料摘要（简介/粉丝数/头像/账号创建时间），供 Early Bird wiki
 * 和项目卡片展示用。跟 getTwitterBioWebsite 是同一个后端接口，但那个函数只关心网站
 * URL 这一个字段，这里需要展示用的完整信息，所以另开一个函数，不动那个的返回契约。
 */
export async function fetchTwitterProfileSummary(twitterHandleOrUrl) {
  if (!twitterHandleOrUrl) return null;
  try {
    const handle = normalizeTwitterHandle(twitterHandleOrUrl);
    if (!handle || handle.length < 2) return null;
    const r = await fetch(`${RD_BASE}/api/twitter-bios?usernames=${encodeURIComponent(handle)}`);
    if (!r.ok) return null;
    const data = await r.json();
    const bioData = data?.bios?.[`@${handle}`] || data?.bios?.[handle];
    if (!bioData || typeof bioData !== 'object' || (!bioData.bio && !bioData.followers && !bioData.avatar)) return null;
    return {
      handle,
      bio: bioData.bio || '',
      followers: bioData.followers ?? null,
      avatar: bioData.avatar || '',
      createdAt: bioData.created_at || null,
    };
  } catch { return null; }
}

// 将 RootData teamMembers 转换为 founder 结构作为种子
// 关键设计：RootData 团队数据先进 founders 数组（source='rootdata'），
// 后续 Layer A/E 通过 name 匹配合并增强；Layer B 推文分析自动应用到所有人；
// 团队摘要也能基于 RootData 数据触发。
export function seedFoundersFromRootData(members) {
  if (!Array.isArray(members) || !members.length) return [];
  return members.map(m => {
    // RootData twitter 字段可能是完整 URL 或 handle，统一转 @handle 形式
    let twitter = '';
    if (m.twitter) {
      const handle = String(m.twitter)
        .replace(/^https?:\/\/(twitter|x)\.com\//i, '')
        .replace(/^@/, '')
        .split('/')[0]
        .split('?')[0]
        .trim();
      if (handle && handle.length >= 2) twitter = '@' + handle;
    }
    return {
      name:       m.name || '',
      role:       m.title || '',
      bio:        (m.description || '').slice(0, 250),
      avatarUrl:  m.logo || m.avatarUrl || '',
      profileUrl: m.rootdataUrl || '',
      twitter,
      github:     '',
      linkedin:   m.linkedIn || m.linkedin || '',
      priorWork:  [],
      sources:    ['rootdata'],
    };
  }).filter(f => f.name);
}

// 从 details.social_media 直接抽取 GitHub owner/repo（不依赖 fetchGithubSignal 结果，
// 让 fetchEnhancedTeam 能与主 Promise.all 完全并行）。
export function extractGithubOwnerRepo(details) {
  const sm = details?.social_media || {};
  let ghUrl = null;
  for (const val of Object.values(sm)) {
    if (typeof val === 'string' && val.toLowerCase().includes('github.com')) {
      ghUrl = val; break;
    }
  }
  if (!ghUrl) return null;
  const m = ghUrl.match(/github\.com\/([^\/\s]+)(?:\/([^\/\s\?#]+))?/);
  if (!m) return null;
  return { owner: m[1], repo: (m[2] || '').replace(/\.git$/, '') };
}

// ── Layer B: Twitter 推文深度分析 ──────────────────────
//  对 Layer A/C 识别出的 founder（含 twitter 字段）拉最近 N 条推文，
//  Claude 提取技术深度 / 市场判断力 / 信噪比 / 前作识别。

// 工具：handle 规范化
export function normalizeTwitterHandle(handleOrUrl) {
  if (!handleOrUrl) return '';
  return String(handleOrUrl)
    .replace(/^https?:\/\/(twitter|x)\.com\//i, '')
    .replace(/^@/, '')
    .split('/')[0]
    .split('?')[0]
    .trim()
    .toLowerCase();
}

// 批量拉 founders 的 tweets（一次 API 调用，最多 5 人 × 30 推文）
export async function fetchFounderTweets(handles, count = 30) {
  const cleanHandles = (Array.isArray(handles) ? handles : [])
    .map(normalizeTwitterHandle)
    .filter(h => h && h.length >= 2);
  if (!cleanHandles.length) return {};
  try {
    const r = await fetch(`${RD_BASE}/api/twitter-tweets?usernames=${encodeURIComponent(cleanHandles.join(','))}&count=${count}`, {
      signal: typeof AbortSignal !== 'undefined' && AbortSignal.timeout ? AbortSignal.timeout(20000) : undefined,
    });
    if (!r.ok) return {};
    const data = await r.json();
    if (!data?.ok) return {};
    // 归一化 key（去 @ 前缀，小写）
    const out = {};
    Object.entries(data.tweets || {}).forEach(([k, v]) => {
      out[k.replace(/^@/, '').toLowerCase()] = Array.isArray(v) ? v : [];
    });
    return out;
  } catch { return {}; }
}

// 让 Claude 对单个 founder 的推文做深度分析。
// 输出 schema:
// {
//   techDepth: 0-10,        // 技术内容密度（讨论协议/算法/代码细节）
//   marketJudgment: 0-10,   // 市场判断力（曾预判牛熊/叙事拐点）
//   signalToNoise: 0-10,    // 信噪比（信号内容 vs 自我营销）
//   sentiment: -1.0~1.0,    // 整体情绪
//   topTopics: string[],    // 高频讨论话题（3-5 个，每个 ≤ 12 字）
//   priorWork: string[],    // 从推文中识别的前作（每条 ≤ 20 字）
//   redFlags: string[],     // 风险信号（如 shilling / 频繁夸大）
//   greenFlags: string[],   // 正面信号（如持续技术贡献）
//   summary: string         // 一句话总结 ≤ 50 字
// }
export async function analyzeFounderTweets(handle, tweets, founderName, projectName) {
  if (!Array.isArray(tweets) || tweets.length === 0) return null;

  // 截取最近 30 条，简化字段降低 prompt 体积
  const limited = tweets.slice(0, 30).map(t => {
    const text = (t.text || '').replace(/\s+/g, ' ').slice(0, 280);
    const date = t.created_at ? t.created_at.slice(0, 10) : '?';
    const eng  = (t.like_count || 0) + (t.retweet_count || 0);
    return `[${date} likes=${t.like_count || 0} rt=${t.retweet_count || 0}] ${text}`;
  }).join('\n');

  const prompt = `分析「${founderName || '@' + handle}」（${projectName ? projectName + ' 创始人/团队成员' : '团队成员'}）的最近 ${tweets.length} 条推文。
严格按 JSON 输出，不要任何额外解释。

推文列表（最新在前）：
${limited}

评估维度（0-10 整数）：
- techDepth：技术内容密度。讨论协议/算法/代码/架构细节越多越高；纯营销/MEME 内容低。
- marketJudgment：市场判断力。曾预判过 BTC/ETH/叙事拐点（早于共识半年）= 8+；只是事后吹嘘 = 2。
- signalToNoise：信号噪音比。真知灼见 vs 自我营销/拉踩竞品/转 GIF。
- sentiment：-1.0~1.0 浮点，整体情绪倾向。

字段：
- topTopics：3-5 个高频话题，每个 ≤ 12 字（如 "ZK Rollup"、"AMM 设计"、"VC 流动性"）
- priorWork：明确提到的前作项目/公司（"Previously at Coinbase"、"Founded Foo"）
- redFlags：发现的风险（如 "频繁 shill 山寨"、"删除负面推文"、"持续转 NFT 广告"）
- greenFlags：正面信号（如 "持续解释技术细节"、"参与多个开源协议讨论"）
- summary：一句话总结 ≤ 50 字

输出 JSON：
{
  "techDepth": 0-10,
  "marketJudgment": 0-10,
  "signalToNoise": 0-10,
  "sentiment": -1.0~1.0,
  "topTopics": ["topic1", "topic2"],
  "priorWork": ["At Coinbase 2019-22"],
  "redFlags": [],
  "greenFlags": ["持续技术输出"],
  "summary": "..."
}`;

  try {
    const res = await fetch(`${RD_BASE}/api/ai/score`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: 'claude-opus-4-7',
        max_tokens: 1000,
        messages: [{ role: 'user', content: prompt }],
      }),
    });
    if (!res.ok) {
      console.warn(`[team-B-tweets-ai] ❌ HTTP ${res.status}`);
      return null;
    }
    const ai = await res.json();
    // Q60
    if (ai._fallback) console.warn(`[team-B-tweets-ai] ⚠️ DeepSeek 兜底, 原因: ${ai._fallback_reason || '未知'}`);
    else console.log(`[team-B-tweets-ai] ✅ ${ai.model || 'claude'} OK`);
    const text = (ai.content || []).map(b => b?.type === 'text' ? b.text : '').join('');
    const cleaned = text.replace(/```json\s*/g, '').replace(/```\s*/g, '').trim();
    const m = cleaned.match(/\{[\s\S]*\}/);
    if (!m) return null;
    const parsed = JSON.parse(m[0]);
    return {
      techDepth:       Math.max(0, Math.min(10, Math.round(parsed.techDepth ?? 0))),
      marketJudgment:  Math.max(0, Math.min(10, Math.round(parsed.marketJudgment ?? 0))),
      signalToNoise:   Math.max(0, Math.min(10, Math.round(parsed.signalToNoise ?? 0))),
      sentiment:       Math.max(-1, Math.min(1, parsed.sentiment ?? 0)),
      topTopics:       Array.isArray(parsed.topTopics)  ? parsed.topTopics.slice(0, 5).filter(Boolean)  : [],
      priorWork:       Array.isArray(parsed.priorWork)  ? parsed.priorWork.slice(0, 5).filter(Boolean)  : [],
      redFlags:        Array.isArray(parsed.redFlags)   ? parsed.redFlags.slice(0, 5).filter(Boolean)   : [],
      greenFlags:      Array.isArray(parsed.greenFlags) ? parsed.greenFlags.slice(0, 5).filter(Boolean) : [],
      summary:         (parsed.summary || '').slice(0, 100),
      tweetCount:      tweets.length,
    };
  } catch { return null; }
}

// ══════════════════════════════════════════════════════
//  Layer E — BrightData LinkedIn People 抓取
//
//  触发条件：details.social_media.linkedin 存在（项目有公开 LinkedIn 公司页）
//  数据源：BrightData LinkedIn Companies dataset
//  返回：员工列表（含 name / position / profile_url / about / location / experience）
//  过滤：保留 CEO / CTO / Founder / Engineer / Designer / Lead 等核心角色
// ══════════════════════════════════════════════════════

// 工具：从 details.social_media 抽 LinkedIn company URL
export function extractLinkedInCompanyUrl(details) {
  const sm = details?.social_media || {};
  for (const val of Object.values(sm)) {
    if (typeof val === 'string' && /linkedin\.com\/company\//i.test(val)) {
      return val.replace(/\/+$/, '');
    }
  }
  return null;
}

// 调 proxy 拿 LinkedIn 员工列表
// 返回 { pending, employees, cached, error?, status?, detail? }
// 让 fetchEnhancedTeam 能把失败原因写到 debug.linkedinError 让用户看到
export async function fetchLinkedInPeople(companyUrl) {
  if (!companyUrl) return { pending: false, employees: [], error: 'no company URL' };
  try {
    const r = await fetch(`${RD_BASE}/api/linkedin-people?url=${encodeURIComponent(companyUrl)}`, {
      signal: typeof AbortSignal !== 'undefined' && AbortSignal.timeout ? AbortSignal.timeout(20000) : undefined,
    });
    // 即使 !ok 也读 body 拿错误原因（proxy 出错时返回 {ok:false, error, detail}）
    let data = null;
    try { data = await r.json(); } catch {}
    if (!r.ok) {
      if (r.status === 202) return { pending: true, employees: [], snapshot_id: data?.snapshot_id, status: data?.status };
      return {
        pending: false,
        employees: [],
        error: data?.error || `HTTP ${r.status}`,
        reason: data?.reason || null, // 'company_not_found' | 'brightdata_unreachable' | 'trigger_unknown'
        detail: data?.detail || null,
        status: r.status,
      };
    }
    if (!data?.ok || !Array.isArray(data.employees)) {
      return { pending: false, employees: [], error: 'unexpected response', detail: data };
    }
    return { pending: false, employees: data.employees, cached: !!data.cached };
  } catch (e) {
    return { pending: false, employees: [], error: e?.message || 'fetch failed' };
  }
}

// 把 BrightData LinkedIn 员工映射为 founder 结构
// 过滤规则：保留核心岗位，过滤 advisor / investor / intern 等
export function mapLinkedInEmployeesToFounders(employees) {
  if (!Array.isArray(employees) || !employees.length) return [];
  const CORE_ROLE_RE = /\b(founder|cofounder|co-founder|co founder|ceo|cto|coo|cfo|head|lead|director|vp|president|principal|chief|engineer|developer|designer|architect|product|operations)\b/i;
  const FILTER_RE   = /\b(advisor|advisory|investor|partner|backer|intern|customer|press|recruiter)\b/i;

  return employees
    .filter(e => e.name && e.position)
    .filter(e => CORE_ROLE_RE.test(e.position) && !FILTER_RE.test(e.position))
    .slice(0, 12)
    .map(e => {
      // 从 experience 抽前作（最多 2 个）
      const priorWork = (e.experience || [])
        .slice(1, 3) // 跳过当前职位
        .map(exp => {
          const company = exp.company || exp.organization || '';
          const title = exp.title || exp.position || '';
          if (!company) return null;
          const dateRange = exp.start_date && exp.end_date
            ? `${exp.start_date}-${exp.end_date}`
            : exp.start_date ? `since ${exp.start_date}` : '';
          return `${title ? title + ' @' : ''} ${company} ${dateRange}`.trim().slice(0, 30);
        })
        .filter(Boolean);

      return {
        name:      e.name,
        role:      e.position.split(/[,|]/)[0].trim().slice(0, 60),
        bio:       e.about ? e.about.slice(0, 250) : '',
        avatarUrl: e.avatar || '',
        profileUrl: e.profile_url || '',
        twitter:   '',
        github:    '',
        linkedin:  e.profile_url || '',
        priorWork: priorWork,
        sources:   ['linkedin'],
      };
    });
}

// ══════════════════════════════════════════════════════
//  Layer F — AI 知识库召回（最终兜底，零成本）
//
//  触发条件：所有上层（RootData seed + A 官网 + C GitHub + D Web 搜索 + E LinkedIn）都没找到
//  数据源：Claude/DeepSeek 训练数据里关于知名 Web3 项目的记忆
//  适用场景：>1 年的主流融资项目（90% Web3 投研对象）
//  限制：模型 cutoff 之后的新项目召回不到；模型可能产生幻觉名字 → 必须保守
// ══════════════════════════════════════════════════════

export async function recallTeamFromAIKnowledge(projectName, oneLiner = '', tags = []) {
  if (!projectName || projectName.length < 3) return [];

  const tagsStr = Array.isArray(tags) && tags.length ? tags.slice(0, 5).join(', ') : '';
  const prompt = `你是 Web3 投研助手。基于你的训练数据知识，回忆「${projectName}」项目的创始团队信息。
${oneLiner ? `\n项目简介：${oneLiner.slice(0, 200)}` : ''}
${tagsStr ? `项目标签：${tagsStr}` : ''}

## 严格规则
1. **只回答你高度确信的事实**。不知道就返回 {"founders": []}，绝不编造。
2. 优先级：你**亲眼读过的**融资新闻 / 项目官方公告 / 创始人公开发言 > 通用 Web3 知识 > 推测。
3. 同名项目防混淆：${projectName} 在 Web3 领域可能与其他项目重名，**必须确认是同一个项目**才回答。
4. 时效性：训练数据 cutoff 后发布的新项目你不应该知道，老老实实返回空数组。

## 输出 JSON（严格格式）
{
  "founders": [
    {
      "name": "Full Name",
      "role": "CEO / Co-Founder / CTO / Engineer / ...",
      "bio": "≤200 字，背景一句话",
      "twitter": "@handle (确认知道，否则空字符串)",
      "github": "username (确认知道，否则空)",
      "linkedin": "URL (确认知道，否则空)",
      "priorWork": ["前作 ≤25 字", "..."]
    }
  ],
  "confidence": "high" | "medium" | "low",
  "knowledgeNote": "一句话说明你的信息来源（如 '2024 年 a16z 领投 $20M 时的公告'）"
}

## 关键约束
- **宁缺勿滥**：返回 0 个 founder 比返回错的好。
- **匿名团队就标空**：如果项目本身就是匿名团队（如部分 DeFi 项目），founders=[]，confidence="low"。
- confidence="low" 时，整个数组也建议留空。`;

  try {
    // Q53: Layer F 依赖 Claude 训练数据的 Web3 知识，DeepSeek 兜底反而拉低召回率
    //      （DeepSeek 对 "Hyperliquid Jeffrey Yan" 这类小众事实知识弱）。noFallback=true
    //      让 lanyi 403/timeout 时直接报错，避免错答"AI 不知道该项目"。
    const res = await fetch(`${RD_BASE}/api/ai/score?noFallback=true`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: 'claude-opus-4-7',
        max_tokens: 1500,
        messages: [{ role: 'user', content: prompt }],
      }),
    });
    if (!res.ok) {
      console.warn(`[team-F-recall-ai] ❌ HTTP ${res.status} (noFallback=true，无 DeepSeek 兜底)`);
      return [];
    }
    const ai = await res.json();
    // Q60：noFallback=true 时 ai._fallback 应永远为 false，若出现需排查 proxy
    if (ai._fallback) console.warn(`[team-F-recall-ai] ⚠️ 异常：noFallback=true 但仍走兜底, 原因: ${ai._fallback_reason || '未知'}`);
    else console.log(`[team-F-recall-ai] ✅ ${ai.model || 'claude'} OK`);
    const text = (ai.content || []).map(b => b?.type === 'text' ? b.text : '').join('');
    const cleaned = text.replace(/```json\s*/g, '').replace(/```\s*/g, '').trim();
    const m = cleaned.match(/\{[\s\S]*\}/);
    if (!m) return [];
    let parsed;
    try { parsed = JSON.parse(m[0]); } catch { return []; }
    // confidence=low 时整体丢弃，避免低质量信息污染
    if (parsed?.confidence === 'low') return [];
    const founders = Array.isArray(parsed?.founders) ? parsed.founders : [];
    return founders
      .filter(f => f?.name && f.name.length >= 2)
      .slice(0, 8)
      .map(f => ({
        name:      f.name,
        role:      f.role || '',
        bio:       (f.bio || '').slice(0, 250),
        avatarUrl: '',
        profileUrl: '',
        twitter:   f.twitter || '',
        github:    f.github || '',
        linkedin:  f.linkedin || '',
        priorWork: Array.isArray(f.priorWork) ? f.priorWork.slice(0, 5).filter(Boolean) : [],
        sources:   ['ai-knowledge'],
        _aiKnowledgeNote: parsed.knowledgeNote || '',
        _confidence: parsed.confidence || 'medium',
      }));
  } catch { return []; }
}

// ══════════════════════════════════════════════════════
//  Layer D — Web 搜索兜底（DDG HTML SERP + Twitter v2 搜索）
//
//  触发条件：Layer A + Layer C 都没找到 founder 时，作为最后一道防线。
//  数据源：
//   1) DuckDuckGo HTML SERP（免费、无 API key，但 Railway IP 容易被限流）
//   2) Twitter v2 /tweets/search/recent（已有 bearer，免费）
//  并行运行，Claude 合成后写入 enhancedTeam.founders（source='websearch'）
// ══════════════════════════════════════════════════════

// 用现有 /api/fetch-html 抓 DuckDuckGo HTML SERP
export async function searchDuckDuckGoSnippets(query) {
  if (!query || query.length < 3) return [];
  const url = `https://html.duckduckgo.com/html/?q=${encodeURIComponent(query)}`;
  try {
    const r = await fetch(`${RD_BASE}/api/fetch-html?url=${encodeURIComponent(url)}`, {
      signal: typeof AbortSignal !== 'undefined' && AbortSignal.timeout ? AbortSignal.timeout(15000) : undefined,
    });
    if (!r.ok) return [];
    const data = await r.json();
    if (!data?.ok || !data.html) return [];
    // 解析 DDG HTML 结果（class="result__title" 和 class="result__snippet"）
    const html = data.html;
    const items = [];
    const titleRe   = /<a[^>]*class="result__a"[^>]*href="([^"]+)"[^>]*>([\s\S]*?)<\/a>/gi;
    const snippetRe = /<a[^>]*class="result__snippet"[^>]*>([\s\S]*?)<\/a>/gi;
    const titles = [];
    let m;
    while ((m = titleRe.exec(html)) !== null && titles.length < 10) {
      titles.push({ url: m[1], title: m[2].replace(/<[^>]+>/g, '').trim() });
    }
    const snippets = [];
    while ((m = snippetRe.exec(html)) !== null && snippets.length < 10) {
      snippets.push(m[1].replace(/<[^>]+>/g, '').trim());
    }
    for (let i = 0; i < titles.length; i++) {
      items.push({ ...titles[i], snippet: snippets[i] || '' });
    }
    return items.slice(0, 10);
  } catch { return []; }
}

// 用现有 Twitter v2 /tweets/search/recent 找项目相关推文
export async function searchTwitterForTeam(projectName) {
  if (!projectName || projectName.length < 3) return [];
  // 查询：项目名 + (founder OR CEO OR CTO OR launched OR co-founded)
  const q = `"${projectName}" (founder OR CEO OR CTO OR co-founder)`;
  try {
    const r = await fetch(`${RD_BASE}/api/twitter-search?q=${encodeURIComponent(q)}&count=30`, {
      signal: typeof AbortSignal !== 'undefined' && AbortSignal.timeout ? AbortSignal.timeout(15000) : undefined,
    });
    if (!r.ok) return [];
    const data = await r.json();
    if (!data?.ok || !Array.isArray(data.tweets)) return [];
    // 取相关性较高的推文（含 founder/CEO 关键词的）
    // 注意 "Co-founded" 是过去式，"founded" 也常出现 → 加 founded/cofounded 提升召回
    const KEYWORDS = /\b(founder|founded|cofounder|cofounded|co-founder|co-founded|ceo|cto|launched|building)\b/i;
    return data.tweets
      .filter(t => KEYWORDS.test(t.text))
      .slice(0, 15);
  } catch { return []; }
}

// 让 Claude 从 DDG snippets + Twitter 推文中抽 founder 信息
export async function extractTeamFromWebSearch(ddgItems, tweets, projectName) {
  if ((!ddgItems || !ddgItems.length) && (!tweets || !tweets.length)) return null;
  const ddgDigest = (ddgItems || []).slice(0, 10).map((it, i) =>
    `[DDG${i+1}] ${it.title}\n  URL: ${it.url}\n  ${it.snippet?.slice(0, 200) || ''}`
  ).join('\n');
  const twDigest = (tweets || []).slice(0, 15).map((t, i) =>
    `[TW${i+1}] @${t.author_username}${t.author_verified ? '✓' : ''}: ${t.text?.replace(/\s+/g, ' ').slice(0, 280) || ''}`
  ).join('\n');

  const prompt = `从下方 Web 搜索结果 + Twitter 推文中抽取「${projectName}」项目的创始人/团队信息。
严格按 JSON 输出。

## DuckDuckGo 搜索结果（${ddgItems?.length || 0} 条）：
${ddgDigest || '（无）'}

## Twitter 推文（${tweets?.length || 0} 条）：
${twDigest || '（无）'}

## 提取规则：
1. 只抽取**明确提到**是「${projectName}」项目的 founder/CEO/CTO/co-founder/lead engineer
   的人名。不要把 Web3 KOL / 媒体编辑当成创始人。
2. 姓名识别：两个或更多英文单词首字母大写（如 "Seni Thomas"）。Web3 项目偶有
   单名 handle（如 "vitalik"），允许但标 role 时注明。
3. 角色：明确说"founder"/"co-founder"/"CEO"/"CTO" 才算。"backed by" / "partner"
   不算 founder。
4. 链接抽取：DDG snippet 里 linkedin.com/in/X / twitter.com/X / x.com/X / github.com/X
   抽到对应字段。
5. bio：从 snippet / 推文里找信号最强的一句（前作/学历/领域），限 200 字。
6. priorWork：明确前作/学校/公司，每条 ≤ 25 字。
7. sourceEvidence：标注信息来自哪个搜索结果（如 "DDG3, TW5"），便于 UI 显示信任度。
8. **保守原则**：不确定就不写。整段空时返回 {"founders": []}。

## 输出 JSON：
{
  "founders": [
    {
      "name": "Full Name",
      "role": "CEO / Co-Founder / ...",
      "bio": "≤200 chars or empty",
      "twitter": "@handle or empty",
      "github": "username or empty",
      "linkedin": "URL or empty",
      "priorWork": ["item ≤25 chars"],
      "sourceEvidence": "DDG2, TW5"
    }
  ]
}`;

  try {
    // Q53: Layer D 抽取也走 noFallback — 命名实体识别（NER）任务对 Claude 远好于 DeepSeek
    const res = await fetch(`${RD_BASE}/api/ai/score?noFallback=true`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: 'claude-opus-4-7',
        max_tokens: 1200,
        messages: [{ role: 'user', content: prompt }],
      }),
    });
    if (!res.ok) {
      console.warn(`[team-D-search-ai] ❌ HTTP ${res.status} (noFallback=true)`);
      return null;
    }
    const ai = await res.json();
    // Q60
    if (ai._fallback) console.warn(`[team-D-search-ai] ⚠️ 异常：noFallback=true 但仍走兜底, 原因: ${ai._fallback_reason || '未知'}`);
    else console.log(`[team-D-search-ai] ✅ ${ai.model || 'claude'} OK`);
    const text = (ai.content || []).map(b => b?.type === 'text' ? b.text : '').join('');
    const cleaned = text.replace(/```json\s*/g, '').replace(/```\s*/g, '').trim();
    const m = cleaned.match(/\{[\s\S]*\}/);
    if (!m) return null;
    const parsed = JSON.parse(m[0]);
    const founders = Array.isArray(parsed?.founders) ? parsed.founders : [];
    return founders.filter(f => f?.name).slice(0, 8);
  } catch { return null; }
}

// Layer D 入口：并行跑 多网页搜索 query + Twitter，合成
// Q55: 单 query "founder OR CEO ..." 召回太低（特别是被 DDG 限流时只能拿到一条结果）。
//      改为 3 个语义互补的 query 并行：
//       1) 通用 founder 站点定向（linkedin/twitter）— 找名片
//       2) "CEO interview" — 找访谈类深度文章（TechCrunch / Coindesk）
//       3) "team profile" — 找官方/媒体的团队介绍
//      合并去重后喂给 Claude 抽取，召回率显著提升。
//
// 2026-07-09：原来这里走 searchDuckDuckGoSnippets（DDG HTML SERP），后来发现
// DDG 收紧了反自动化策略，生产环境查询全量被拦截（见 earlyBirdWiki.js 同期修复的
// 详细验证过程）。换成 Surf 的 /search/web（真实存在、已验证的网页搜索端点）。
export async function fetchLayerDTeam(projectName, debug = null) {
  if (!projectName) return [];
  const t0 = Date.now();
  const webQueries = [
    `${projectName} founder CEO CTO linkedin twitter`,
    `${projectName} CEO interview`,
    `${projectName} team profile`,
  ];
  const webPromises = webQueries.map(q => surfWebSearch(q)
    .then(res => Array.isArray(res?.data) ? res.data : [])
    .catch(() => []));
  const [webResults, tweets] = await Promise.all([
    Promise.all(webPromises),
    searchTwitterForTeam(projectName).catch(() => []),
  ]);
  // 合并 + 去重：按 URL 去重
  const seen = new Set();
  const webItems = [];
  for (const arr of webResults) {
    for (const it of (arr || [])) {
      if (it?.url && !seen.has(it.url)) {
        seen.add(it.url);
        // 归一化成 extractTeamFromWebSearch 已有的 {title,url,snippet} 契约，
        // 那个函数本身不用改，也不用改它自己的测试
        webItems.push({ title: it.title, url: it.url, snippet: it.description || '' });
      }
    }
  }
  // 限制总量给 Claude，避免 prompt 过长
  webItems.length = Math.min(webItems.length, 15);
  if (debug) {
    debug.layerDWebCount = webItems.length;
    debug.layerDWebQueries = webQueries.length;
    debug.layerDWebPerQuery = webResults.map(r => (r || []).length);
    debug.layerDTweetCount = tweets.length;
  }
  if (webItems.length === 0 && tweets.length === 0) {
    if (debug) debug.timings.layerDMs = Date.now() - t0;
    return [];
  }
  const founders = await extractTeamFromWebSearch(webItems, tweets, projectName);
  if (debug) {
    debug.timings.layerDMs = Date.now() - t0;
    debug.layerDFoundersCount = (founders || []).length;
  }
  return founders || [];
}

// ── 团队质量摘要合成（Layer A/B/C 完成后一次 Claude 调用）──
//  输入：founders + topContributors + RootData teamMembers
//  输出：qualityScore (0-100) + strengths/concerns/oneLineSummary/
//        hasDoxxedFounders/prominentBackgrounds
//
//  设计：让 AI 综合所有信号（人数 / 实名度 / 前作含金量 / 推文活跃度 /
//  GitHub 提交模式）给出团队质量画像。展示在 UI 团队增强卡之上。
export async function synthesizeTeamSummary({ founders = [], topContributors = [], rootDataMembers = [], projectName = '' }) {
  const totalSignal = founders.length + topContributors.length + rootDataMembers.length;
  if (totalSignal === 0) return null;

  // 紧凑序列化：每人 ~120 字，最多 15 人 ≈ 1.8k 字符
  const foundersDigest = founders.slice(0, 10).map(f => {
    const parts = [`${f.name || '?'} (${f.role || '?'})`];
    if (f.bio)            parts.push(`bio: ${f.bio.slice(0, 100)}`);
    if (f.priorWork?.length) parts.push(`prior: ${f.priorWork.join('; ')}`);
    if (f.twitter)        parts.push(`tw: ${f.twitter}`);
    if (f.github)         parts.push(`gh: ${f.github}`);
    if (f.linkedin)       parts.push(`li: ✓`);
    if (f.tweetAnalysis)  parts.push(`tweets: tech=${f.tweetAnalysis.techDepth}/10 mkt=${f.tweetAnalysis.marketJudgment}/10 sn=${f.tweetAnalysis.signalToNoise}/10`);
    parts.push(`src: ${(f.sources || []).join(',')}`);
    return parts.join(' | ');
  }).join('\n');

  const contribDigest = topContributors.slice(0, 5).map(c =>
    `${c.username} (${c.name || c.username}) commits=${c.commitCount} followers=${c.followers}${c.bio ? ' bio: ' + c.bio.slice(0, 60) : ''}`
  ).join('\n');

  const rootDigest = rootDataMembers.slice(0, 6).map(m =>
    `${m.name || '?'}${m.title ? ' (' + m.title + ')' : ''}${m.description ? ' - ' + m.description.slice(0, 80) : ''}${m.twitter ? ' tw=' + m.twitter : ''}`
  ).join('\n');

  const prompt = `你是 Web3 投资分析师。基于下方团队画像数据，对「${projectName}」项目团队做综合质量评估。
严格按 JSON 输出。

## Layer A 官网抓取（${founders.length} 人）：
${foundersDigest || '（无）'}

## Layer C GitHub 贡献者（${topContributors.length} 人）：
${contribDigest || '（无）'}

## RootData 团队成员（${rootDataMembers.length} 人）：
${rootDigest || '（无）'}

## 评估要求：

1. **qualityScore (0-100)**：综合考量
   - 实名度：有真实姓名+前作 > 全匿名
   - 规模：3+ 核心成员 > 单人项目
   - 含金量：顶级前作（Coinbase / a16z / ConsenSys / Polygon / 协议核心团队）+5~+15
   - 技术深度：GitHub 高频提交 + 推文 techDepth>=7 加分
   - 红旗：全匿名 / 单人 / 缺技术背景 / 推文 redFlags 含 shilling 扣分

2. **hasDoxxedFounders (bool)**：至少 1 人是真实姓名（非匿名）

3. **teamSize (number)**：核心团队人数（合并去重后）

4. **prominentBackgrounds**：从前作里提炼最具说服力的 3-5 个（如 "Ex-Coinbase"、"Ex-Polygon Labs"）

5. **strengths (≤4 条)**：每条 ≤ 25 字
   例："CEO 前 Coinbase 5 年"、"10+ 工程师全实名"、"GitHub 持续高频提交"

6. **concerns (≤3 条)**：每条 ≤ 25 字
   例："创始人推特活跃度低"、"核心团队均无 Web3 前作"、"GitHub 贡献集中在 1 人"

7. **oneLineSummary**：一句话团队画像，≤ 60 字
   例："西方 Web3 老兵团队，CEO 前 Coinbase，10+ 工程师，技术深度足"

## 输出 JSON（严格）：
{
  "qualityScore": 0-100,
  "hasDoxxedFounders": true|false,
  "teamSize": number,
  "prominentBackgrounds": ["Ex-Coinbase","..."],
  "strengths": ["…", "…"],
  "concerns": ["…"],
  "oneLineSummary": "…"
}`;

  try {
    const res = await fetch(`${RD_BASE}/api/ai/score`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        model: 'claude-opus-4-7',
        max_tokens: 800,
        messages: [{ role: 'user', content: prompt }],
      }),
    });
    if (!res.ok) {
      console.warn(`[team-summary-ai] ❌ HTTP ${res.status}`);
      return null;
    }
    const ai = await res.json();
    // Q60
    if (ai._fallback) console.warn(`[team-summary-ai] ⚠️ DeepSeek 兜底, 原因: ${ai._fallback_reason || '未知'}`);
    else console.log(`[team-summary-ai] ✅ ${ai.model || 'claude'} OK`);
    const text = (ai.content || []).map(b => b?.type === 'text' ? b.text : '').join('');
    const cleaned = text.replace(/```json\s*/g, '').replace(/```\s*/g, '').trim();
    const m = cleaned.match(/\{[\s\S]*\}/);
    if (!m) return null;
    const parsed = JSON.parse(m[0]);
    const clamp = (v, lo, hi) => Math.max(lo, Math.min(hi, Math.round(v)));
    // 客观 teamSize: 合并 founders + rootDataMembers 按 name 去重的真实计数。
    // AI 给的 teamSize 不可靠（之前 RootData 4 人 + Layer A 1 人 AI 仍输出 1），
    // 这里硬覆盖。topContributors 不算"团队"，只算"贡献者"，不参与去重计数。
    const norm = s => String(s || '').toLowerCase().replace(/[^a-z0-9]/g, '');
    const nameSet = new Set();
    for (const f of founders)         if (f?.name) nameSet.add(norm(f.name));
    for (const m of rootDataMembers)  if (m?.name) nameSet.add(norm(m.name));
    const objectiveTeamSize = nameSet.size;
    return {
      qualityScore:         clamp(parsed.qualityScore ?? 0, 0, 100),
      hasDoxxedFounders:    !!parsed.hasDoxxedFounders,
      teamSize:             objectiveTeamSize > 0 ? objectiveTeamSize : Math.max(0, parseInt(parsed.teamSize ?? totalSignal, 10) || 0),
      prominentBackgrounds: Array.isArray(parsed.prominentBackgrounds) ? parsed.prominentBackgrounds.slice(0, 5).filter(Boolean) : [],
      strengths:            Array.isArray(parsed.strengths) ? parsed.strengths.slice(0, 4).filter(Boolean) : [],
      concerns:             Array.isArray(parsed.concerns)  ? parsed.concerns.slice(0, 3).filter(Boolean)  : [],
      oneLineSummary:       (parsed.oneLineSummary || '').slice(0, 80),
    };
  } catch { return null; }
}

// ── 主入口：编排 Layer A + Layer C + Layer B ───────────
//  返回 enhancedTeam:
//  {
//    founders:        [{name, role, ..., sources, tweetAnalysis?}],
//    topContributors: [{username, ...}] (raw GitHub data),
//    sourcesUsed:     ['website'|'github'|'twitter'],
//    confidence:      'high'|'medium'|'low',
//    scrapedTeamPageUrl: string|null,
//    layersRun:       ['A','C','B'?],
//  }
//
//  设计：Phase 2 加 Layer B Twitter 推文分析；Layer D Brave 兜底 + 合成层留 Phase 3。
//  githubSignal 参数可选：传了就复用其 owner/repo，没传则从 details 自己抽。
export async function fetchEnhancedTeam(details, githubSignal = null) {
  // 诊断对象：捕获 Layer A 全流程中间状态。
  // 写入 enhancedTeam._debug 字段，UI 端可通过「🔍 诊断」按钮查看，
  // 也会用 console.log 打到浏览器 DevTools（用 [team-debug] 前缀好搜）。
  const debug = {
    websiteProbed:        details?.social_media?.website || null,
    probedCandidates:     [],
    winnerUrl:            null,
    twitterFallbackUsed:  false,
    twitterBioUrl:        null,
    compressedLen:        null,
    compressedSample:     null,
    claudeStatus:         null,
    claudeRawText:        null,
    claudeError:          null,
    claudeFoundersCount:  null,
    githubAttempted:      false,
    githubContributors:   0,
    layerBAttempted:      false,
    layerBHandles:        [],
    errors:               [],
    timings:              {},
  };

  const out = {
    founders:           [],
    topContributors:    [],
    sourcesUsed:        [],
    confidence:         'low',
    scrapedTeamPageUrl: null,
    layersRun:          ['A', 'C'],
    companyStats:       null,  // Q30：LinkedIn Company 统计（pre-TGE 项目重要）
    _debug:             debug,
  };

  const _projectLabel = details?.project_name || 'unknown';
  const _safeLog = (...args) => {
    try { if (typeof console !== 'undefined' && console.log) console.log('[team-debug]', _projectLabel, ...args); } catch {}
  };

  // 种子：把 RootData 团队成员先放进 founders（sources=['rootdata']）
  // Layer A/E 通过 name 匹配合并（追加 sources），Layer B 自动应用到 RootData twitter handle
  // 团队摘要也能基于 RootData 数据触发
  const rootDataSeed = seedFoundersFromRootData(details?._rootDataMembers || []);
  if (rootDataSeed.length > 0) {
    out.founders = rootDataSeed;
    if (!out.sourcesUsed.includes('rootdata')) out.sourcesUsed.push('rootdata');
    if (!out.layersRun.includes('RootData')) out.layersRun.unshift('RootData');
    debug.rootDataSeedCount = rootDataSeed.length;
    _safeLog('rootData seed', { count: rootDataSeed.length });
  } else {
    debug.rootDataSeedCount = 0;
  }

  // Layer E: BrightData LinkedIn People（与 Layer A 并行）
  // 触发条件：details.social_media 里有 LinkedIn company URL
  const linkedinCompanyUrl = extractLinkedInCompanyUrl(details);
  debug.linkedinCompanyUrl = linkedinCompanyUrl;
  const linkedinPromise = linkedinCompanyUrl
    ? (async () => {
        const tE0 = Date.now();
        const result = await fetchLinkedInPeople(linkedinCompanyUrl);
        debug.timings.layerEMs = Date.now() - tE0;
        debug.linkedinPending  = !!result?.pending;
        debug.linkedinEmployeeCount = result?.employees?.length || 0;
        // 把错误原因写到 debug 让用户在 🔍 诊断弹窗能看到
        if (result?.error) {
          debug.linkedinError = result.error;
          debug.linkedinErrorDetail = result.detail;
          _safeLog('layer E error', { error: result.error, detail: result.detail, ms: debug.timings.layerEMs });
        }
        if (!result || result.error) return [];
        if (result.pending) {
          _safeLog('layer E pending (BrightData snapshot still running)');
          return [];
        }
        const mapped = mapLinkedInEmployeesToFounders(result.employees);
        debug.linkedinMappedCount = mapped.length;
        // Q30：聚合 company stats（员工总数 + 工程岗 vs 业务岗 + 招聘信号）
        const allEmp = result.employees || [];
        const ENG_RE = /\b(engineer|developer|architect|sde|swe|cto|tech\s*lead)\b/i;
        const BIZ_RE = /\b(bd|sales|marketing|growth|business|operations|hr|finance|legal)\b/i;
        const HIRING_RE = /\b(hiring|recruit|talent\s*acquisition|we'?re\s*hiring)\b/i;
        let engCount = 0, bizCount = 0, hiringSignal = false;
        for (const emp of allEmp) {
          const pos = (emp.position || '').toLowerCase();
          const about = (emp.about || '').toLowerCase();
          if (ENG_RE.test(pos)) engCount++;
          else if (BIZ_RE.test(pos)) bizCount++;
          if (HIRING_RE.test(pos) || HIRING_RE.test(about)) hiringSignal = true;
        }
        out.companyStats = {
          totalEmployeesSeen: allEmp.length,  // BrightData 返回上限（不是真总数）
          engineeringCount: engCount,
          businessCount: bizCount,
          hiringSignal,                       // 至少 1 人 bio/title 有招聘字眼
          source: 'brightdata_linkedin_people',
        };
        debug.linkedinCompanyStats = out.companyStats;
        _safeLog('layer E done', { employees: result.employees.length, mapped: mapped.length, eng: engCount, biz: bizCount, hiring: hiringSignal, cached: result.cached, ms: debug.timings.layerEMs });
        return mapped;
      })().catch(e => { debug.errors.push(`linkedin: ${e.message}`); return []; })
    : Promise.resolve([]);

  // Layer A: 官网团队页 → Claude 提取
  // 兜底链：social_media.website → Twitter bio → 项目名派生备用站点（Q54）
  // Q54 背景：RootData 给的 website 经常是"基金会"站（hyperfoundation.org）
  //          而真正放团队页的是产品站（hyperliquid.xyz）。从项目名 slug 派生
  //          .xyz/.io/.com 等候选，可挽救这类案例。
  const tA0 = Date.now();
  const primaryWebsite = details?.social_media?.website;
  let teamPageProbe = await probeTeamPage(primaryWebsite, debug);
  if (!teamPageProbe) {
    const projectTwitter = details?.social_media?.X
                        || details?.social_media?.twitter
                        || details?.social_media?.x;
    if (projectTwitter) {
      const bioWebsite = await getTwitterBioWebsite(projectTwitter);
      debug.twitterBioUrl = bioWebsite;
      if (bioWebsite && bioWebsite !== primaryWebsite) {
        teamPageProbe = await probeTeamPage(bioWebsite, debug);
        if (teamPageProbe) debug.twitterFallbackUsed = true;
      }
    }
  }
  if (!teamPageProbe) {
    // Q54: 从项目名派生 4 个 TLD 候选，串行试（避免并发太多 fetch-html）
    const alternates = deriveProjectAlternateSites(details?.project_name || '', primaryWebsite);
    debug.alternateSitesGenerated = alternates;
    for (const altUrl of alternates) {
      const probe = await probeTeamPage(altUrl, debug);
      if (probe) {
        teamPageProbe = probe;
        debug.alternateFallbackUsed = altUrl;
        _safeLog('Layer A alternate hit', { url: altUrl });
        break;
      }
    }
  }
  debug.timings.probeMs = Date.now() - tA0;
  _safeLog('probe done', { probedCount: debug.probedCandidates.length, winner: debug.winnerUrl, twitterFallback: debug.twitterFallbackUsed, alternate: debug.alternateFallbackUsed || null, ms: debug.timings.probeMs });

  if (teamPageProbe) {
    out.scrapedTeamPageUrl = teamPageProbe.url;
    if (debug.twitterFallbackUsed) out.layersRun.push('A-twitter-bio');
    const tExt0 = Date.now();
    const extracted = await extractTeamFromHtml(teamPageProbe.html, details?.project_name || '', teamPageProbe.url, debug);
    debug.timings.extractMs = Date.now() - tExt0;
    _safeLog('extract done', { compressedLen: debug.compressedLen, claudeStatus: debug.claudeStatus, foundersCount: debug.claudeFoundersCount, error: debug.claudeError, ms: debug.timings.extractMs });
    if (extracted?.founders?.length) {
      // 与已 seed 的 RootData founders 合并（按 name 匹配；找不到则追加）
      const existingNameLower = new Set(out.founders.map(f => (f.name || '').toLowerCase()));
      extracted.founders.forEach(wf => {
        const lowerName = (wf.name || '').toLowerCase();
        if (existingNameLower.has(lowerName)) {
          const existing = out.founders.find(f => f.name?.toLowerCase() === lowerName);
          if (existing) {
            if (!existing.role && wf.role) existing.role = wf.role;
            if (!existing.bio && wf.bio) existing.bio = wf.bio;
            if (!existing.avatarUrl && wf.avatarUrl) existing.avatarUrl = wf.avatarUrl;
            if (!existing.profileUrl && wf.profileUrl) existing.profileUrl = wf.profileUrl;
            if (!existing.twitter && wf.twitter) existing.twitter = wf.twitter;
            if (!existing.github && wf.github) existing.github = wf.github;
            if (!existing.linkedin && wf.linkedin) existing.linkedin = wf.linkedin;
            if (wf.priorWork?.length && !existing.priorWork?.length) existing.priorWork = wf.priorWork;
            if (!existing.sources.includes('website')) existing.sources.push('website');
          }
        } else {
          out.founders.push({ ...wf, sources: ['website'] });
          existingNameLower.add(lowerName);
        }
      });
      if (!out.sourcesUsed.includes('website')) out.sourcesUsed.push('website');
    }
  } else {
    _safeLog('no team page found', { website: debug.websiteProbed, twitterBio: debug.twitterBioUrl });
  }

  // 合并 Layer E (LinkedIn) 结果到 founders
  // 策略：name 已在 Layer A founders 中 → 合并 sources（追加 linkedin），补 LinkedIn 字段；
  //       name 不在 → 单独追加（sources=['linkedin']）
  const linkedinFounders = await linkedinPromise;
  if (linkedinFounders.length > 0) {
    const existingNameLower = new Set(out.founders.map(f => (f.name || '').toLowerCase()));
    linkedinFounders.forEach(lf => {
      const lowerName = (lf.name || '').toLowerCase();
      if (existingNameLower.has(lowerName)) {
        // 合并：补 linkedin / avatar / bio / experience 等
        const existing = out.founders.find(f => f.name?.toLowerCase() === lowerName);
        if (existing) {
          if (!existing.linkedin && lf.linkedin) existing.linkedin = lf.linkedin;
          if (!existing.avatarUrl && lf.avatarUrl) existing.avatarUrl = lf.avatarUrl;
          if (!existing.bio && lf.bio) existing.bio = lf.bio;
          if (lf.priorWork?.length && !existing.priorWork?.length) existing.priorWork = lf.priorWork;
          if (!existing.sources.includes('linkedin')) existing.sources.push('linkedin');
        }
      } else {
        out.founders.push(lf);
        existingNameLower.add(lowerName);
      }
    });
    if (!out.sourcesUsed.includes('linkedin')) out.sourcesUsed.push('linkedin');
    out.layersRun.push('E');
  }

  // Layer C: GitHub 前 3 贡献者（owner/repo 优先用 githubSignal，否则从 details 自抽）
  const ghIds = (githubSignal?.owner && githubSignal?.repo)
    ? { owner: githubSignal.owner, repo: githubSignal.repo }
    : extractGithubOwnerRepo(details);
  if (ghIds && ghIds.repo) {
    debug.githubAttempted = true;
    const tC0 = Date.now();
    const contributors = await fetchTopContributors(ghIds.owner, ghIds.repo, 3);
    debug.timings.githubMs = Date.now() - tC0;
    debug.githubContributors = contributors.length;
    _safeLog('github contributors', { owner: ghIds.owner, repo: ghIds.repo, count: contributors.length });
    if (contributors.length) {
      out.topContributors = contributors;
      out.sourcesUsed.push('github');

      // 把 GitHub bio 强匹配的并入 founders，弱匹配单独显示
      // "强匹配"：username/name 与现有 founder twitter/github/name 任一吻合 → 合并 sources
      // 否则 founders 中没有的且有 bio 的 contributor 单独追加（标 source=github）
      const existingGhSet  = new Set(out.founders.map(f => (f.github || '').toLowerCase()).filter(Boolean));
      const existingTwSet  = new Set(out.founders.map(f => (f.twitter || '').replace(/^@/, '').toLowerCase()).filter(Boolean));
      const existingNameSet = new Set(out.founders.map(f => (f.name || '').toLowerCase()));

      contributors.forEach(c => {
        const ghLower = c.username.toLowerCase();
        const twLower = (c.twitter || '').replace(/^@/, '').toLowerCase();
        const nameLower = (c.name || '').toLowerCase();

        // 强匹配：直接增强 founder
        const matched = out.founders.find(f =>
          (f.github && f.github.toLowerCase() === ghLower) ||
          (twLower && f.twitter && f.twitter.replace(/^@/, '').toLowerCase() === twLower) ||
          (f.name && f.name.toLowerCase() === nameLower)
        );
        if (matched) {
          if (!matched.github) matched.github = c.username;
          if (!matched.twitter && c.twitter) matched.twitter = '@' + c.twitter;
          if (!matched.bio && c.bio) matched.bio = c.bio.slice(0, 80);
          if (!matched.sources.includes('github')) matched.sources.push('github');
        } else if (c.bio || c.commitCount > 10) {
          // 弱匹配但 bio 有内容或者提交数显著 → 单独列出
          if (!existingGhSet.has(ghLower) && !existingNameSet.has(nameLower)) {
            out.founders.push({
              name:      c.name || c.username,
              role:      c.commitCount >= 100 ? 'Top Contributor' : 'Contributor',
              bio:       c.bio.slice(0, 80),
              twitter:   c.twitter ? '@' + c.twitter : '',
              github:    c.username,
              linkedin:  '',
              priorWork: c.company ? [c.company] : [],
              sources:   ['github'],
            });
          }
        }
      });
    }
  }

  // ── Layer B: Twitter 推文深度分析 ────────────────────
  // 对 founders 中带 twitter 的（最多前 3 人）批量拉推文 + 并行 Claude 分析
  const tweetTargets = out.founders
    .filter(f => f.twitter)
    .slice(0, 3)
    .map(f => ({ founder: f, handle: normalizeTwitterHandle(f.twitter) }))
    .filter(t => t.handle);

  if (tweetTargets.length > 0) {
    debug.layerBAttempted = true;
    debug.layerBHandles = tweetTargets.map(t => t.handle);
    const tB0 = Date.now();
    const handles = tweetTargets.map(t => t.handle);
    const tweetsByHandle = await fetchFounderTweets(handles, 30);
    const hasAnyTweets = Object.values(tweetsByHandle).some(arr => arr.length > 0);
    if (hasAnyTweets) {
      // 并行 Claude 分析（每人独立 prompt，避免 10500 字符上限）
      await Promise.all(tweetTargets.map(async ({ founder, handle }) => {
        const tweets = tweetsByHandle[handle] || [];
        if (tweets.length === 0) return;
        const analysis = await analyzeFounderTweets(handle, tweets, founder.name, details?.project_name);
        if (analysis) {
          founder.tweetAnalysis = analysis;
          if (!founder.sources.includes('twitter')) founder.sources.push('twitter');
        }
      }));
      if (out.founders.some(f => f.tweetAnalysis)) {
        out.sourcesUsed.push('twitter');
        out.layersRun.push('B');
      }
    }
    debug.timings.layerBMs = Date.now() - tB0;
    _safeLog('layer B done', { handles, analyzed: out.founders.filter(f => f.tweetAnalysis).length });
  }

  // ── Layer D 兜底：当 A + C 都没识别到任何 founder 时触发
  // （RootData 团队也空的项目最需要兜底）
  if (out.founders.length === 0 && out.topContributors.length === 0) {
    const tD0 = Date.now();
    const webFounders = await fetchLayerDTeam(details?.project_name || '', debug);
    debug.timings.layerDMs = Date.now() - tD0;
    if (webFounders.length > 0) {
      out.founders = webFounders.map(f => ({ ...f, sources: ['websearch'] }));
      out.sourcesUsed.push('websearch');
      out.layersRun.push('D');
      _safeLog('layer D done', { count: webFounders.length, web: debug.layerDWebCount, tweets: debug.layerDTweetCount });
    } else {
      _safeLog('layer D empty', { web: debug.layerDWebCount, tweets: debug.layerDTweetCount });
    }
  }

  // ── Layer F 最终兜底：AI 知识库召回（零成本）
  // 触发条件：所有上层都没找到 founder（RootData seed/A/C/D/E 全空）
  // 适用：知名 Web3 项目（>1 年 + 主流融资）
  if (out.founders.length === 0 && out.topContributors.length === 0) {
    const tF0 = Date.now();
    debug.layerFAttempted = true;
    const aiFounders = await recallTeamFromAIKnowledge(
      details?.project_name || '',
      details?.one_liner || details?.description || '',
      details?.tags || []
    );
    debug.timings.layerFMs = Date.now() - tF0;
    debug.layerFFoundersCount = aiFounders.length;
    if (aiFounders.length > 0) {
      out.founders = aiFounders;
      out.sourcesUsed.push('ai-knowledge');
      out.layersRun.push('F');
      out.aiKnowledgeNote = aiFounders[0]?._aiKnowledgeNote || '';
      out.aiKnowledgeConfidence = aiFounders[0]?._confidence || 'medium';
      _safeLog('layer F done', { count: aiFounders.length, confidence: out.aiKnowledgeConfidence, ms: debug.timings.layerFMs });
    } else {
      _safeLog('layer F empty (AI 不知道该项目)');
    }
  }

  // 置信度：三源高、双源中-高、单源中、零源低
  const sourceCount = new Set(out.sourcesUsed).size;
  if (sourceCount >= 3)      out.confidence = 'high';
  else if (sourceCount === 2) out.confidence = 'high';
  else if (sourceCount === 1) out.confidence = 'medium';

  // ── 合成层：团队质量摘要（基于 Layer A/B/C + RootData 团队）
  // 在 founders 或 topContributors 非空时触发，给出 qualityScore + strengths + concerns
  if (out.founders.length > 0 || out.topContributors.length > 0) {
    try {
      const tS0 = Date.now();
      // rootDataMembers 已被种子进 founders，避免 prompt 里重复输入
      const summary = await synthesizeTeamSummary({
        founders:        out.founders,
        topContributors: out.topContributors,
        rootDataMembers: debug.rootDataSeedCount > 0 ? [] : (Array.isArray(details?._rootDataMembers) ? details._rootDataMembers : []),
        projectName:     details?.project_name || '',
      });
      debug.timings.summaryMs = Date.now() - tS0;
      if (summary) {
        out.summary = summary;
        out.layersRun.push('Summary');
      }
      _safeLog('summary done', { qualityScore: summary?.qualityScore, ms: debug.timings.summaryMs });
    } catch (e) {
      debug.errors.push(`summary: ${e.message}`);
    }
  }

  return out;
}
